import { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { PublicDemoProvider } from './data/PublicDemoContext'
import { AnalyticsOverviewPage } from './pages/AnalyticsOverviewPage'
import { CustomerPage } from './pages/CustomerPage'
import { InventoryPage } from './pages/InventoryPage'
import { LandingPage } from './pages/LandingPage'
import { MarketingPage } from './pages/MarketingPage'
import { MetricsPage } from './pages/MetricsPage'
import { OperationsPage } from './pages/OperationsPage'
import { SalesPage } from './pages/SalesPage'
import { I18nProvider, localeFromPath, stripLocale, useI18n } from './i18n'

function ScrollManager() {
  const location = useLocation()
  const { copy, locale } = useI18n()
  const pageTitles: Record<string, string> = {
    '/analytics/overview': copy.overview.title,
    '/analytics/sales': copy.sales.title,
    '/analytics/customer': copy.customer.title,
    '/analytics/marketing': copy.marketing.title,
    '/analytics/inventory': copy.inventory.title,
    '/analytics/operations': copy.operations.title,
    '/analytics/metrics': copy.metrics.title,
  }

  useEffect(() => {
    const canonicalPath = stripLocale(location.pathname)
    document.title = pageTitles[canonicalPath]
      ? `${pageTitles[canonicalPath]} | Commerce Intelligence`
      : locale === 'zh' ? 'Commerce Intelligence | 可信的管理决策' : 'Commerce Intelligence | Trusted Executive Decisions'
    if (location.hash) {
      const scrollToAnchor = () => document.getElementById(location.hash.slice(1))?.scrollIntoView()
      const frame = requestAnimationFrame(scrollToAnchor)
      const retry = window.setTimeout(scrollToAnchor, 150)
      return () => { cancelAnimationFrame(frame); window.clearTimeout(retry) }
    }
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [location.pathname, location.hash, locale, copy])

  return null
}

export function AppRoutes() {
  const location = useLocation()
  const locale = localeFromPath(location.pathname)
  return (
    <I18nProvider locale={locale}>
      <PublicDemoProvider>
        <ScrollManager />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/zh" element={<LandingPage />} />
          <Route path="/analytics/overview" element={<AnalyticsOverviewPage />} />
          <Route path="/analytics/sales" element={<SalesPage />} />
          <Route path="/analytics/customer" element={<CustomerPage />} />
          <Route path="/analytics/marketing" element={<MarketingPage />} />
          <Route path="/analytics/inventory" element={<InventoryPage />} />
          <Route path="/analytics/operations" element={<OperationsPage />} />
          <Route path="/analytics/metrics" element={<MetricsPage />} />
          <Route path="/zh/analytics/overview" element={<AnalyticsOverviewPage />} />
          <Route path="/zh/analytics/sales" element={<SalesPage />} />
          <Route path="/zh/analytics/customer" element={<CustomerPage />} />
          <Route path="/zh/analytics/marketing" element={<MarketingPage />} />
          <Route path="/zh/analytics/inventory" element={<InventoryPage />} />
          <Route path="/zh/analytics/operations" element={<OperationsPage />} />
          <Route path="/zh/analytics/metrics" element={<MetricsPage />} />
          <Route path="*" element={<Navigate to={locale === 'zh' ? '/zh' : '/'} replace />} />
        </Routes>
      </PublicDemoProvider>
    </I18nProvider>
  )
}

function App() {
  return <BrowserRouter><AppRoutes /></BrowserRouter>
}

export default App
